import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  getSessionMetadata,
  readAndClearExpiredFlag,
  subscribeSessionMetadata,
  type SessionMetadata,
} from "../auth/session";
import { buildSafeDebugBundle, copySafeDebugToClipboard } from "../auth/debug";
import { BUILD_ID } from "../buildInfo";
import { ReauthNotice, useInvalidation } from "./ReauthNotice";

interface Props {
  children: React.ReactNode;
}

/** Wraps a screen that needs an admin session. Reads the session
 *  metadata (NOT the token) — components NEVER see the bearer.
 *
 *  When the session is absent, this guard shows a "Login and
 *  return here" affordance whose link includes
 *  `?returnTo=<currentPath>`. The Login page reads that query and,
 *  on successful verify + BFF probe, navigates back to the
 *  destination rather than bouncing to /session. The operator
 *  never bounces between pages manually.
 *
 *  When the API client has just cleared the session due to a
 *  401 on a bearer-attached request, this guard reads the
 *  one-shot expired flag and renders a distinct "session expired
 *  or invalid" notice rather than the generic "please login"
 *  prompt. The notice fires once per expiry. */
export function AuthRequired({ children }: Props) {
  const location = useLocation();
  const [session, setSession] = useState<SessionMetadata | null>(
    getSessionMetadata(),
  );
  // The expired flag is one-shot per session-expiry; we read it on
  // mount AND every time the subscription fires (in case the
  // session was cleared while this guard was the active route).
  const [wasExpired, setWasExpired] = useState<boolean>(() =>
    readAndClearExpiredFlag(),
  );
  const invalidation = useInvalidation();

  useEffect(
    () =>
      subscribeSessionMetadata((md) => {
        setSession(md);
        if (md === null) {
          // Session just cleared — pick up the expired flag if it
          // was set (e.g. by the API client's 401 handler).
          const expired = readAndClearExpiredFlag();
          if (expired) setWasExpired(true);
        }
      }),
    [],
  );

  // Children unmount the moment the session goes. That is what discards any
  // in-flight signing or proposal state a page was holding (§3): there is no
  // separate "clear signer state" step because the state does not survive the
  // guard flipping. A page that cached such state OUTSIDE React would defeat
  // this; none does.
  if (session !== null) {
    return <>{children}</>;
  }

  // Build the returnTo target so the operator lands back on the
  // exact page they tried to open. Preserves the query string and
  // hash so deep links survive the round trip.
  const here = `${location.pathname}${location.search}${location.hash}`;
  const loginHref = `/login?returnTo=${encodeURIComponent(here)}`;

  function onCopySafeDebug(): void {
    copySafeDebugToClipboard(
      buildSafeDebugBundle({ route: location.pathname, returnTo: here }),
    );
  }

  return (
    <div
      style={{
        padding: 16,
        border: "1px solid var(--border)",
        borderRadius: 4,
        background: "var(--bg-card)",
      }}
    >
      {invalidation !== null ? (
        // A wallet event ended this session. That is a more specific — and more
        // actionable — statement than either "expired" or "not logged in", so
        // it wins over both.
        <ReauthNotice />
      ) : wasExpired ? (
        <p
          role="alert"
          style={{
            marginTop: 0,
            padding: 10,
            border: "1px solid var(--danger)",
            color: "var(--danger)",
            borderRadius: 4,
            background: "rgba(255,100,100,0.06)",
          }}
        >
          Session expired or invalid. Please log in again.
        </p>
      ) : (
        <p style={{ marginTop: 0 }}>
          <strong>Login required.</strong> This screen needs an active owner
          session.
        </p>
      )}
      <p style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <Link
          to={loginHref}
          style={{
            display: "inline-block",
            padding: "8px 14px",
            border: "1px solid var(--accent)",
            color: "var(--accent)",
            background: "rgba(255,181,71,0.12)",
            borderRadius: 4,
            textDecoration: "none",
            fontSize: 13,
          }}
        >
          Login and return here
        </Link>
        <span style={{ fontSize: 11, color: "var(--fg-dim)" }}>
          returnTo:&nbsp;<code style={{ fontSize: 11 }}>{here}</code>
        </span>
      </p>
      <hr />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <button onClick={onCopySafeDebug} style={{ fontSize: 12 }}>
          Copy safe auth debug
        </button>
        <span style={{ fontSize: 10, color: "var(--fg-dim)" }}>
          (no token, no signature, no HMAC)
        </span>
      </div>
      <p style={{ fontSize: 10, color: "var(--fg-dim)", marginTop: 8 }}>
        build id: <code>{BUILD_ID}</code>
      </p>
    </div>
  );
}
